import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { importService } from '../../../services/importService';

const DataValidationPanel = ({ leads }) => {
  const [validationResults, setValidationResults] = useState({
    valid: [],
    invalid: []
  });
  const [validating, setValidating] = useState(false);

  useEffect(() => {
    validateLeads();
  }, [leads]);

  const validateLeads = async () => {
    setValidating(true);
    const valid = [];
    const invalid = [];

    leads?.forEach((lead) => {
      const validation = importService?.validateLead({
        nombre: lead?.nombre,
        email: lead?.email,
        telefono: lead?.telefono,
        presupuesto: lead?.presupuesto
      });

      if (validation?.valid) {
        valid?.push(lead);
      } else {
        invalid?.push({
          lead,
          errors: validation?.errors
        });
      }
    });

    setValidationResults({ valid, invalid });
    setValidating(false);
  };

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-6">Data Validation</h2>

      {validating ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Validating leads...</p>
        </div>
      ) : (
        <div>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Leads</p>
                  <p className="text-3xl font-bold text-gray-900">{leads?.length}</p>
                </div>
                <AlertCircle className="w-10 h-10 text-blue-600" />
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Valid Leads</p>
                  <p className="text-3xl font-bold text-green-600">{validationResults?.valid?.length}</p>
                </div>
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Invalid Leads</p>
                  <p className="text-3xl font-bold text-red-600">{validationResults?.invalid?.length}</p>
                </div>
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
            </div>
          </div>

          {/* Invalid Leads List */}
          {validationResults?.invalid?.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Leads with Validation Errors</h3>
              <div className="space-y-4">
                {validationResults?.invalid?.map(({ lead, errors }) => (
                  <div key={lead?.id} className="border border-red-200 rounded-lg p-4 bg-red-50">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium text-gray-900">{lead?.nombre}</p>
                        <p className="text-sm text-gray-600">{lead?.email}</p>
                      </div>
                      <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                        {errors?.length} error{errors?.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <ul className="list-disc list-inside space-y-1">
                      {errors?.map((error, index) => (
                        <li key={index} className="text-sm text-red-700">{error}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {validationResults?.invalid?.length === 0 && (
            <div className="text-center py-12 bg-green-50 rounded-lg">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">All Leads Valid!</h3>
              <p className="text-gray-600">No validation errors found in your lead database</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DataValidationPanel;